// src/pages/offlineSales/terminal/hooks/useCustomerDisplaySync.js
import { useEffect, useRef, useCallback } from 'react';
import {
  getCustomerDisplayChannelName,
  getCustomerDisplayUrl,
  CUSTOMER_DISPLAY_WINDOW_NAME,
} from '../../customerDisplay/customerDisplayChannel';

/**
 * Pushes the terminal's cart / totals / last sale to the customer-facing display window
 * and returns `openCustomerDisplay`, which opens that window (on the second screen when the
 * browser allows it).
 */
const useCustomerDisplaySync = ({
  shop, customer, cartItems, totals, orderDiscount, completedOrder, showReceiptModal,
}) => {
  const channelRef = useRef(null);
  const stateRef = useRef(null);
  const displayWindowRef = useRef(null);

  const totalDiscount = (totals.itemDiscounts || 0) + (parseFloat(orderDiscount) || 0) + (totals.loyaltyDiscount || 0);

  let mode = 'idle';
  if (showReceiptModal && completedOrder) mode = 'paid';
  else if (cartItems.length > 0) mode = 'cart';

  const state = {
    mode,
    shopName: shop?.name || '',
    customerName: customer && !customer.isCashSales ? customer.name : null,
    items: cartItems.map(i => ({
      key: i.key,
      name: i.baseName || i.name,
      variationLabel: i.variationLabel || null,
      sku: i.sku || null,
      image: i.image || null,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      lineDiscount: parseFloat(i.lineDiscount) || 0,
      lineTotal: i.quantity * i.unitPrice - (parseFloat(i.lineDiscount) || 0),
    })),
    itemCount: cartItems.length,
    subtotal: totals.subtotal || 0,
    totalDiscount,
    grandTotal: totals.grandTotal || 0,
    paid: mode === 'paid'
      ? {
          orderNumber: completedOrder.orderNumber,
          grandTotal: parseFloat(completedOrder.grandTotal) || 0,
          paidAmount: parseFloat(completedOrder.paidAmount) || 0,
          changeAmount: parseFloat(completedOrder.changeAmount) || 0,
          balanceAmount: parseFloat(completedOrder.balanceAmount) || 0,
        }
      : null,
  };
  stateRef.current = state;

  // Open the channel per shop; answer 'hello' from a freshly opened display
  useEffect(() => {
    if (!shop?.id || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(getCustomerDisplayChannelName(shop.id));
    channel.onmessage = (e) => {
      if (e.data?.type === 'hello') channel.postMessage({ type: 'state', state: stateRef.current });
    };
    channelRef.current = channel;
    return () => { channel.close(); channelRef.current = null; };
  }, [shop?.id]);

  // Push every change
  const serialized = JSON.stringify(state);
  useEffect(() => {
    channelRef.current?.postMessage({ type: 'state', state: JSON.parse(serialized) });
  }, [serialized]);

  const openCustomerDisplay = useCallback(async () => {
    if (!shop?.id) return;
    const existing = displayWindowRef.current;
    if (existing && !existing.closed) {
      existing.focus();
      return;
    }

    const url = getCustomerDisplayUrl(shop.id);
    let features = 'popup,width=1024,height=768';

    // Window Management API (Chrome): place the window full screen on the other monitor.
    // The first time, Chrome asks the cashier to allow "window management".
    if ('getScreenDetails' in window) {
      try {
        const details = await window.getScreenDetails();
        const other = details.screens.find(s => s !== details.currentScreen);
        if (other) {
          features = [
            'popup',
            `left=${other.availLeft}`,
            `top=${other.availTop}`,
            `width=${other.availWidth}`,
            `height=${other.availHeight}`,
            'fullscreen',
          ].join(',');
        }
      } catch {
        // permission denied / unsupported — fall back to a normal popup the cashier can drag over
      }
    }

    displayWindowRef.current = window.open(url, CUSTOMER_DISPLAY_WINDOW_NAME, features);
  }, [shop?.id]);

  return { openCustomerDisplay };
};

export default useCustomerDisplaySync;
